// Part of salesPages, split by screen to keep files short; ../salesPages.ts merges the parts.
// Keys sit directly under salesPages, in sub-objects whose names no other part uses.
// The product/service drawer and the customer/vendor drawer.
export const en = {
  // Shared by both drawers.
  recordDrawer: {
    saved: '{{name}} saved.',
  },
  itemDrawer: {
    titleEdit: 'Product/service information',
    description:
      'Defaults that sales and purchase lines start with: description, price, account and VAT.',
    copyName: '{{name}} (copy)',
    kindHint: {
      service: 'Work you do: consulting, installation, hours, fees.',
      non_inventory: 'Goods you buy and sell without tracking quantities.',
      inventory: 'Goods you stock: quantity on hand, FIFO cost of goods sold.',
    },
    sacCode: 'SAC code',
    hsnCode: 'HSN code',
    sacHint: 'Services accounting code: 6 digits starting 99.',
    hsnHint: '4, 6 or 8 digits; printed on GST tax invoices.',
    uqc: 'Unit (UQC)',
    uqcHint: 'For GST returns: NOS, KGS, MTR, SET…',
    currencyHint: 'Foreign-currency prices are coming soon.',
    quantityOnHand: 'Quantity on hand',
    initialQuantity: 'Initial quantity on hand',
    asOfDate: 'As of date',
    reorderPoint: 'Reorder point',
    inventoryAssetAccount: 'Inventory asset account *',
    openingNote:
      'The initial quantity is valued at the cost below and posted to opening balance equity on the as-of date.',
    sellCheckbox: 'I sell this product/service to my customers.',
    salesDescription: 'Description on sales forms',
    salesDescriptionAr: 'Arabic description on sales forms',
    salesPrice: 'Sales price/rate ({{currency}})',
    incomeAccount: 'Income account *',
    salesTax: 'Sales {{tax}}',
    salesTaxHint: "Used on new invoice lines; a customer's default {{tax}} treatment wins.",
    fromIncomeAccount: 'From the income account',
    purchasing: 'Purchasing',
    purchaseCheckbox: 'I purchase this product/service from a vendor.',
    purchaseDescription: 'Description on purchase forms',
    purchaseDescriptionAr: 'Arabic description on purchase forms',
    cost: 'Cost ({{currency}})',
    cogsAccount: 'Cost of goods sold account *',
    expenseAccount: 'Expense account *',
    purchaseTax: 'Purchase {{tax}}',
    fromExpenseAccount: 'From the expense account',
    preferredVendor: 'Preferred vendor',
    viewAll: 'View products and services',
  },
  contactDrawer: {
    title: { customer: 'Customer information', vendor: 'Vendor information' },
    description: {
      customer: 'Add a customer to use on transactions.',
      vendor: 'Add a vendor to use on transactions.',
    },
    view: { customer: 'View customer', vendor: 'View vendor' },
    nameAndContact: 'Name and contact',
    displayName: { customer: 'Customer display name *', vendor: 'Vendor display name *' },
    arabicDisplayName: 'Arabic display name',
    phone: 'Phone number',
    mobile: 'Mobile number',
    addresses: 'Addresses',
    shippingSameAsBilling: 'Shipping address same as billing address',
    gstinHint: '15 characters; leave blank for an unregistered person.',
    vatRegistered: {
      customer: 'Customer is registered for UAE VAT',
      vendor: 'Vendor is registered for UAE VAT',
    },
    trnHint: '15 digits; required on tax invoices to VAT-registered customers.',
    outsideIndia:
      'Outside India: no GSTIN. Sales are exports (EXP-LUT); services bought from abroad use the reverse charge (RCMI).',
    outsideUae:
      'Outside the UAE: no TRN. Purchases of services from abroad usually use the reverse charge (RC).',
    defaultTaxHint: {
      customer: 'Used on new sales lines.',
      vendor: 'Used on new purchase lines.',
    },
    fromProductOrAccount: 'From the product or account',
    documentLanguage: 'Document language',
    documentLanguageHint: 'Language of invoices, statements and emails sent to this contact.',
    companyDefault: 'Company default',
    currencyFixed: {
      customer: 'Fixed once the customer has transactions.',
      vendor: 'Fixed once the vendor has transactions.',
    },
    multicurrencyOff:
      'Switch multicurrency on in Company settings › Advanced to use other currencies.',
    homeCurrency: '{{currency}} (home currency)',
    defaultCategoryHint: 'The expense account bills from this vendor start with.',
    openingNote:
      'Opening balances are entered with the opening balance tool (coming soon), not on this form.',
    address: {
      street: { billing: 'Billing street address', shipping: 'Shipping street address' },
      line2: { billing: 'Billing address line 2', shipping: 'Shipping address line 2' },
      city: { billing: 'Billing city', shipping: 'Shipping city' },
      pinCode: { billing: 'Billing PIN code', shipping: 'Shipping PIN code' },
      poBox: { billing: 'Billing P.O. Box', shipping: 'Shipping P.O. Box' },
      country: { billing: 'Billing country', shipping: 'Shipping country' },
      state: { billing: 'Billing state', shipping: 'Shipping state' },
      emirate: { billing: 'Billing emirate', shipping: 'Shipping emirate' },
    },
  },
};

export const ar = {
  recordDrawer: {
    saved: 'تم حفظ {{name}}.',
  },
  itemDrawer: {
    titleEdit: 'بيانات المنتج/الخدمة',
    description:
      'القيم الافتراضية التي تبدأ بها أسطر المبيعات والمشتريات: الوصف والسعر والحساب وVAT.',
    copyName: '{{name}} (نسخة)',
    kindHint: {
      service: 'عمل تؤديه: استشارات، تركيب، ساعات عمل، رسوم.',
      non_inventory: 'بضائع تشتريها وتبيعها دون تتبّع الكميات.',
      inventory: 'بضائع تحتفظ بها في المخزون: الكمية المتوفرة، وتكلفة البضاعة المباعة بطريقة FIFO.',
    },
    sacCode: 'رمز SAC',
    hsnCode: 'رمز HSN',
    sacHint: 'رمز محاسبة الخدمات: 6 أرقام تبدأ بـ 99.',
    hsnHint: '4 أو 6 أو 8 أرقام؛ يُطبع على فواتير GST الضريبية.',
    uqc: 'الوحدة (UQC)',
    uqcHint: 'لإقرارات GST: NOS وKGS وMTR وSET…',
    currencyHint: 'الأسعار بالعملات الأجنبية قريبًا.',
    quantityOnHand: 'الكمية المتوفرة',
    initialQuantity: 'الكمية الافتتاحية المتوفرة',
    asOfDate: 'في تاريخ',
    reorderPoint: 'حد إعادة الطلب',
    inventoryAssetAccount: 'حساب أصل المخزون *',
    openingNote:
      'تُقيَّم الكمية الافتتاحية بالتكلفة أدناه وتُرحَّل إلى حقوق ملكية الأرصدة الافتتاحية في التاريخ المحدد.',
    sellCheckbox: 'أبيع هذا المنتج/الخدمة لعملائي.',
    salesDescription: 'الوصف في نماذج المبيعات',
    salesDescriptionAr: 'الوصف العربي في نماذج المبيعات',
    salesPrice: 'سعر البيع/السعر بالوحدة ({{currency}})',
    incomeAccount: 'حساب الإيرادات *',
    salesTax: '{{tax}} المبيعات',
    salesTaxHint:
      'يُستخدم في أسطر الفواتير الجديدة؛ وتكون الأولوية لمعاملة {{tax}} الافتراضية الخاصة بالعميل.',
    fromIncomeAccount: 'من حساب الإيرادات',
    purchasing: 'المشتريات',
    purchaseCheckbox: 'أشتري هذا المنتج/الخدمة من مورد.',
    purchaseDescription: 'الوصف في نماذج المشتريات',
    purchaseDescriptionAr: 'الوصف العربي في نماذج المشتريات',
    cost: 'تكلفة الشراء ({{currency}})',
    cogsAccount: 'حساب تكلفة البضاعة المباعة *',
    expenseAccount: 'حساب المصروفات *',
    purchaseTax: '{{tax}} المشتريات',
    fromExpenseAccount: 'من حساب المصروفات',
    preferredVendor: 'المورد المفضّل',
    viewAll: 'عرض المنتجات والخدمات',
  },
  contactDrawer: {
    title: { customer: 'بيانات العميل', vendor: 'بيانات المورد' },
    description: {
      customer: 'أضف عميلًا لاستخدامه في المعاملات.',
      vendor: 'أضف موردًا لاستخدامه في المعاملات.',
    },
    view: { customer: 'عرض العميل', vendor: 'عرض المورد' },
    nameAndContact: 'الاسم وبيانات الاتصال',
    displayName: { customer: 'الاسم المعروض للعميل *', vendor: 'الاسم المعروض للمورد *' },
    arabicDisplayName: 'الاسم المعروض بالعربية',
    phone: 'رقم الهاتف',
    mobile: 'رقم الجوال',
    addresses: 'العناوين',
    shippingSameAsBilling: 'عنوان الشحن مطابق لعنوان الفوترة',
    gstinHint: '15 حرفًا؛ اتركه فارغًا للشخص غير المسجّل.',
    vatRegistered: {
      customer: 'العميل مسجّل في VAT في الإمارات',
      vendor: 'المورد مسجّل في VAT في الإمارات',
    },
    trnHint: '15 رقمًا؛ مطلوب في الفواتير الضريبية للعملاء المسجّلين في VAT.',
    outsideIndia:
      'خارج الهند: لا يوجد GSTIN. المبيعات صادرات (EXP-LUT)؛ والخدمات المشتراة من الخارج تخضع للاحتساب العكسي (RCMI).',
    outsideUae:
      'خارج الإمارات: لا يوجد TRN. تخضع مشتريات الخدمات من الخارج عادةً للاحتساب العكسي (RC).',
    defaultTaxHint: {
      customer: 'يُستخدم في أسطر المبيعات الجديدة.',
      vendor: 'يُستخدم في أسطر المشتريات الجديدة.',
    },
    fromProductOrAccount: 'من المنتج أو الحساب',
    documentLanguage: 'لغة المستندات',
    documentLanguageHint: 'لغة الفواتير وكشوف الحساب والرسائل المرسلة إلى جهة الاتصال هذه.',
    companyDefault: 'الافتراضي للشركة',
    currencyFixed: {
      customer: 'تُثبَّت بعد أن تصبح للعميل معاملات.',
      vendor: 'تُثبَّت بعد أن تصبح للمورد معاملات.',
    },
    multicurrencyOff: 'فعّل تعدد العملات من إعدادات الشركة › متقدم لاستخدام عملات أخرى.',
    homeCurrency: '{{currency}} (العملة الأساسية)',
    defaultCategoryHint: 'حساب المصروفات الذي تبدأ به فواتير هذا المورد.',
    openingNote:
      'تُدخَل الأرصدة الافتتاحية من أداة الأرصدة الافتتاحية (قريبًا)، وليس من هذا النموذج.',
    address: {
      street: { billing: 'عنوان الفوترة: الشارع', shipping: 'عنوان الشحن: الشارع' },
      line2: { billing: 'عنوان الفوترة: السطر 2', shipping: 'عنوان الشحن: السطر 2' },
      city: { billing: 'عنوان الفوترة: المدينة', shipping: 'عنوان الشحن: المدينة' },
      pinCode: { billing: 'عنوان الفوترة: رمز PIN', shipping: 'عنوان الشحن: رمز PIN' },
      poBox: { billing: 'عنوان الفوترة: صندوق البريد', shipping: 'عنوان الشحن: صندوق البريد' },
      country: { billing: 'عنوان الفوترة: الدولة', shipping: 'عنوان الشحن: الدولة' },
      state: { billing: 'عنوان الفوترة: الولاية', shipping: 'عنوان الشحن: الولاية' },
      emirate: { billing: 'عنوان الفوترة: الإمارة', shipping: 'عنوان الشحن: الإمارة' },
    },
  },
};
